import { useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { ErrorCode, formatDate, submissionCountLabel, type ReferrerPartner } from '@brimo/shared';
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  IconButton,
  MenuItem,
  MenuList,
  Pill,
  Popover,
  Table,
  TableSkeleton,
  useToast,
  type TableColumn,
} from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import { usePermissions } from '../../../../lib/session';
import { OrganizationPage, QueryError } from '../users/OrganizationPage';
import {
  useDeleteReferrerPartner,
  useReferrerPartners,
  useRotateReferrerLink,
  useUpdateReferrerPartner,
} from './api';
import { ReferrerPartnerDialog } from './ReferrerPartnerDialog';
import { ReferrerSubmittersDialog } from './ReferrerSubmittersDialog';
import styles from './ReferrerPartnersPage.module.css';

const DESCRIPTION =
  'Externe Partner ohne eigenes Konto reichen über ihren Link Selbstauskünfte für Interessenten ein. Kontakt und Deal tragen den Partner als Quelle.';
const IN_USE_TEXT =
  'Dieser Partner wird bereits in Kontakten oder Deals verwendet. Deaktivieren Sie ihn stattdessen.';

type Confirm =
  { kind: 'rotate'; partner: ReferrerPartner } | { kind: 'delete'; partner: ReferrerPartner; inUse: boolean };

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Einstellungen › Organisation › „Tippgeber\" (D-248): the referrer partners with contact, default advisor, status,
 * submissions and their standing intake link („Link kopieren\"); create/edit dialog, „Link neu erzeugen\" and
 * „Löschen\" (a partner already used by contacts or deals is deactivated instead). Organization admins only: the
 * settings navigation hides the entry for everybody else and the API answers 403.
 */
export function ReferrerPartnersPage() {
  const { toast } = useToast();
  const permissions = usePermissions();
  const admin = permissions.is('settings.organization', 'yes');
  const partners = useReferrerPartners();
  const update = useUpdateReferrerPartner();
  const rotate = useRotateReferrerLink();
  const remove = useDeleteReferrerPartner();
  const [dialog, setDialog] = useState<{ partner: ReferrerPartner | null } | null>(null);
  const [submittersOf, setSubmittersOf] = useState<ReferrerPartner | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  async function copyLink(partner: ReferrerPartner) {
    const ok = await copyText(partner.intakeUrl);
    toast({
      message: ok
        ? `Link von ${partner.name} kopiert.`
        : `Der Link konnte nicht kopiert werden: ${partner.intakeUrl}`,
    });
  }

  function setActive(partner: ReferrerPartner, active: boolean, after?: () => void) {
    setBanner(null);
    update.mutate(
      { id: partner.id, body: { active } },
      {
        onSuccess: () => {
          toast({
            message: active ? `${partner.name} ist wieder aktiv.` : `${partner.name} ist deaktiviert.`,
          });
          after?.();
        },
        onError: (error) => setBanner(errorMessage(error)),
      },
    );
  }

  function runConfirm() {
    if (!confirm) return;
    setConfirmError(null);
    const { partner } = confirm;
    if (confirm.kind === 'rotate') {
      rotate.mutate(partner.id, {
        onSuccess: (next) => {
          setConfirm(null);
          void copyText(next.intakeUrl).then((ok) =>
            toast({
              message: ok
                ? `Neuer Link für ${next.name} erzeugt und kopiert.`
                : `Neuer Link für ${next.name} erzeugt.`,
            }),
          );
        },
        onError: (error) => setConfirmError(errorMessage(error)),
      });
      return;
    }
    if (confirm.inUse) {
      setActive(partner, false, () => setConfirm(null));
      return;
    }
    remove.mutate(partner.id, {
      onSuccess: () => {
        setConfirm(null);
        toast({ message: `${partner.name} gelöscht.` });
      },
      onError: (error) => {
        if (error instanceof ApiError && error.code === ErrorCode.REFERRER_PARTNER_IN_USE) {
          setConfirm({ kind: 'delete', partner, inUse: true });
        } else {
          setConfirmError(errorMessage(error));
        }
      },
    });
  }

  const columns: TableColumn<ReferrerPartner>[] = [
    {
      id: 'name',
      header: 'Name',
      track: 'minmax(0,1.3fr)',
      cell: (row) => (
        <span className={styles.nameCell}>
          {row.brandColor ? (
            <span className={styles.colorDot} style={{ background: row.brandColor }} aria-hidden="true" />
          ) : null}
          <span className={styles.name}>{row.name}</span>
        </span>
      ),
    },
    {
      id: 'contact',
      header: 'Ansprechpartner',
      track: 'minmax(0,1.4fr)',
      cell: (row) =>
        row.contactName || row.contactEmail ? (
          <span className={styles.muted}>
            {[row.contactName, row.contactEmail].filter(Boolean).join(' · ')}
          </span>
        ) : (
          <span className={styles.subtle}>—</span>
        ),
    },
    {
      id: 'closer',
      header: 'Standard-Berater',
      track: 'minmax(0,1fr)',
      cell: (row) =>
        row.defaultCloserName ? (
          <span className={styles.muted}>{row.defaultCloserName}</span>
        ) : (
          <span className={styles.subtle}>Erste Admin-Person</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      track: '96px',
      cell: (row) => (
        <Pill variant={row.active ? 'success' : 'neutral'} dot>
          {row.active ? 'Aktiv' : 'Inaktiv'}
        </Pill>
      ),
    },
    {
      id: 'submissions',
      header: 'Einreichungen',
      track: '150px',
      cell: (row) => (
        <span className={styles.submissions}>
          <span className={styles.count}>
            {submissionCountLabel(row.submissionCount, row.submitterCount)}
          </span>
          <span className={styles.subtle}>
            {row.lastSubmissionAt ? `zuletzt ${formatDate(row.lastSubmissionAt)}` : 'noch keine'}
          </span>
        </span>
      ),
    },
    {
      id: 'link',
      header: 'Link',
      track: '210px',
      cell: (row) => (
        <span className={styles.linkCell}>
          <Button
            variant="secondary"
            size="small"
            disabled={!row.active}
            title={row.active ? row.intakeUrl : 'Der Partner ist inaktiv.'}
            onClick={() => void copyLink(row)}
          >
            Link kopieren
          </Button>
          <RowMenu
            partner={row}
            onEdit={() => setDialog({ partner: row })}
            onShowSubmitters={() => setSubmittersOf(row)}
            onRotate={() => {
              setConfirmError(null);
              setConfirm({ kind: 'rotate', partner: row });
            }}
            onToggleActive={() => setActive(row, !row.active)}
            onDelete={() => {
              setConfirmError(null);
              setConfirm({ kind: 'delete', partner: row, inUse: false });
            }}
          />
        </span>
      ),
    },
  ];

  const items = partners.data;
  const confirmPending = rotate.isPending || remove.isPending || update.isPending;

  return (
    <OrganizationPage
      title="Tippgeber"
      description={DESCRIPTION}
      actions={
        admin ? (
          <Button onClick={() => setDialog({ partner: null })}>+ Tippgeber-Partner anlegen</Button>
        ) : null
      }
    >
      {partners.isError ? <QueryError error={partners.error} /> : null}
      {banner ? <Banner tone="danger">{banner}</Banner> : null}
      {partners.isPending ? (
        <TableSkeleton
          columns={['minmax(0,1.3fr)', 'minmax(0,1.4fr)', 'minmax(0,1fr)', '96px', '150px', '210px']}
          rows={4}
        />
      ) : null}
      {items && items.length === 0 ? (
        <EmptyState
          title="Noch keine Tippgeber-Partner."
          description="Legen Sie einen Partner an und geben Sie ihm seinen Einreich-Link."
          actions={<Button onClick={() => setDialog({ partner: null })}>+ Tippgeber-Partner anlegen</Button>}
        />
      ) : null}
      {items && items.length > 0 ? (
        <>
          <Table
            label="Tippgeber-Partner"
            columns={columns}
            rows={items}
            rowKey={(row) => row.id}
            density="compact"
            rowClassName={(row) => (row.active ? undefined : styles.rowInactive)}
            className={styles.table}
          />
          <p className={styles.footer}>
            Jeder Link gilt 730 Tage ab Anlage oder Neuerzeugung. Abläufe:{' '}
            {items
              .filter((row) => row.active)
              .map((row) => `${row.name} ${formatDate(row.intakeExpiresAt)}`)
              .join(' · ') || 'keine aktiven Partner'}
          </p>
        </>
      ) : null}

      <ReferrerPartnerDialog
        open={dialog !== null}
        partner={dialog?.partner ?? null}
        onClose={() => setDialog(null)}
        onCreated={(created) => void copyLink(created)}
      />

      <ReferrerSubmittersDialog partner={submittersOf} onClose={() => setSubmittersOf(null)} />

      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        tone={confirm?.kind === 'delete' && !confirm.inUse ? 'danger' : 'warning'}
        title={
          confirm?.kind === 'rotate'
            ? 'Link neu erzeugen?'
            : confirm?.inUse
              ? 'Partner deaktivieren?'
              : 'Partner löschen?'
        }
        width={560}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Abbrechen
            </Button>
            <Button
              variant={confirm?.kind === 'delete' && !confirm.inUse ? 'destructiveFilled' : 'primary'}
              loading={confirmPending}
              onClick={runConfirm}
            >
              {confirm?.kind === 'rotate' ? 'Link neu erzeugen' : confirm?.inUse ? 'Deaktivieren' : 'Löschen'}
            </Button>
          </>
        }
      >
        {confirm?.kind === 'rotate' ? (
          <p className={styles.confirmText}>
            Der bisherige Link wird ungültig. {confirm.partner.name} braucht danach den neuen Link.
          </p>
        ) : confirm?.inUse ? (
          <p className={styles.confirmText}>{IN_USE_TEXT}</p>
        ) : confirm ? (
          <p className={styles.confirmText}>
            {confirm.partner.name} wird endgültig gelöscht; sein Link funktioniert danach nicht mehr.
          </p>
        ) : null}
        {confirmError ? <Banner tone="danger">{confirmError}</Banner> : null}
      </Dialog>
    </OrganizationPage>
  );
}

function RowMenu({
  partner,
  onEdit,
  onShowSubmitters,
  onRotate,
  onToggleActive,
  onDelete,
}: {
  partner: ReferrerPartner;
  onEdit: () => void;
  onShowSubmitters: () => void;
  onRotate: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const run = (action: () => void) => () => {
    setOpen(false);
    action();
  };
  return (
    <>
      <IconButton
        ref={anchor}
        icon={MoreHorizontal}
        label={`Aktionen: ${partner.name}`}
        variant="ghost"
        size={28}
        active={open}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      />
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        placement="bottom-end"
        width={230}
      >
        <MenuList label={`Aktionen: ${partner.name}`} onClose={() => setOpen(false)}>
          <MenuItem label="Bearbeiten" onSelect={run(onEdit)} />
          <MenuItem label="Einreicher anzeigen" onSelect={run(onShowSubmitters)} />
          <MenuItem
            label="Link neu erzeugen"
            description={`Der bisherige Link gilt bis ${formatDate(partner.intakeExpiresAt)}`}
            onSelect={run(onRotate)}
          />
          <MenuItem label={partner.active ? 'Deaktivieren' : 'Aktivieren'} onSelect={run(onToggleActive)} />
          <MenuItem label="Löschen" tone="danger" onSelect={run(onDelete)} />
        </MenuList>
      </Popover>
    </>
  );
}
